import { sentryCaptureException } from '@alfa-bank/site-helpers-error-handlers/sentry-logger/capture-exception';

import { normalizeError } from '../helpers/normalize-error';
import type {
    TControlActionFunction,
    IControlDeps,
    IControlPublicProps,
    TActionTrigger,
} from '../models';
import { addPropsToFunction } from '../helpers/add-props-to-function';
import { createAction } from '../helpers/create-action';

import type { TUseLoadingReturn } from './use-function-notifier';
import { useFunctionNotifier } from './use-function-notifier';

type TUseFlow<ActionType extends string> = IControlDeps<ActionType> &
    Pick<IControlPublicProps<ActionType>, 'action' | 'sentryDisabled'>;

/** Обработка экшенов */
export const useFlow = <ActionType extends string>({
    action,
    actions,
    useData = () => ({ action }),
    middleware = ({ callback }) => callback,
    teamConfig,
    sentryDisabled,
    ...props
}: TUseFlow<ActionType>): TUseLoadingReturn<TActionTrigger> => {
    const actionData = useData({ action });

    const handleAction = createAction({
        actions,
        action,
    });

    const handleMiddleware = middleware({
        callback: handleAction,
    });

    const handleProps = addPropsToFunction({
        callback: handleMiddleware,
        props: {
            ...actionData,
            action,
            ...props,
        },
    });

    const [handler, loading, error] = useFunctionNotifier<TControlActionFunction>({
        callback: handleProps,
    });

    if (error) {
        const normalizedError = normalizeError(error);
        const errorMessage = `Action name: ${action}\r\n${teamConfig.teamName}/${teamConfig.packageName} error: ${normalizedError.message}`;
        if (!sentryDisabled && process.env.CS_CAPTURE_MANUAL_EXCEPTIONS) {
            sentryCaptureException(normalizedError, teamConfig);
        }
        console.error(errorMessage);
    }

    return [handler, loading, error];
};
